/**
 * Shared constants + pure helpers for the work-report (Rapport) UI — S3.
 *
 * REPORT_TYPE_OPTIONS mirrors the backend Literal whitelist in
 * backend/app/schemas/work_report.py (16 values, enforced by the DB
 * CHECK ck_work_reports_report_type). Keep in sync when either side
 * is extended.
 *
 * The list deliberately does NOT match the task categories one-to-one:
 * a report records what was DONE (phone_call, internal,
 * applicant_review), a task what is TO DO (reminder, meeting,
 * document_request). Only the shared categories are aligned.
 *
 * Slice B additions are ADDITIVE — no existing value renamed or removed:
 *   tenant_issue   Mieter-Anliegen — disruption / complaint / escalation
 *   tenant_care    Mieterbetreuung — ongoing support and communication
 *   documentation  Dokumentation — writing internal documentation
 *   inspection     Kontrolle — checking / inspecting
 */
import {
  businessDateTimeToIso,
  isoToBusinessDateInput,
  isoToBusinessTimeInput,
} from "../../../../utils/datetime";

export const REPORT_TYPE_OPTIONS = [
  { value: "general", label: "Allgemein" },
  { value: "viewing", label: "Besichtigung" },
  { value: "handover", label: "Übergabe" },
  { value: "contract", label: "Verträge" },
  { value: "applicant_review", label: "Bewerberprüfung" },
  { value: "tenant_issue", label: "Mieter-Anliegen" },
  { value: "maintenance", label: "Wartung" },
  { value: "administration", label: "Administration" },
  { value: "bookkeeping", label: "Buchhaltung" },
  { value: "phone_call", label: "Telefonat" },
  { value: "internal", label: "Intern" },
  // — Slice B —
  { value: "tenant_care", label: "Mieterbetreuung" },
  { value: "inventory", label: "Inventar" },
  { value: "crm", label: "CRM" },
  { value: "documentation", label: "Dokumentation" },
  { value: "inspection", label: "Kontrolle" },
];

export const REPORT_TYPE_LABELS = Object.fromEntries(
  REPORT_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

/**
 * The one place the report detail path is spelled (D2f.1).
 *
 * Every surface that shows a report — the overview, the calendar, its
 * sidebar, and the shared entity section on six detail pages — routes
 * through this, so the path cannot drift into four slightly different
 * spellings.
 *
 * Returns ``null`` when there is no id. Callers MUST treat null as
 * "render the text, not a link": that is what keeps a half-loaded row
 * from producing a link to ``/admin/work-reports/undefined``, which
 * would look clickable and land on a 404.
 *
 * Only a real WorkReport id is accepted. Nothing is ever derived from
 * the title or any other field.
 */
export function getWorkReportDetailPath(workReportId) {
  const id = typeof workReportId === "string" ? workReportId.trim() : workReportId;
  if (!id) return null;
  return `/admin/work-reports/${id}`;
}

/** Accessible name for the "open this report" link. Matches the
 * "Rapport bearbeiten: X" / "Rapport stornieren: X" phrasing the action
 * buttons already use, so a row reads as three clearly distinct
 * actions on the SAME report rather than three anonymous controls. */
export function openReportLabel(title) {
  const t = (title || "").trim();
  return t ? `Rapport öffnen: ${t}` : "Rapport öffnen";
}

/** Minutes → "1h 30m" (0 → "0h 0m"). Display-only — the server owns
 * the stored duration. */
export function formatMinutes(minutes) {
  const m = Number.isFinite(minutes) ? Math.max(0, Math.trunc(minutes)) : 0;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * Combine a date input value (YYYY-MM-DD) and a time input value
 * (HH:MM) into a UTC ISO string, interpreting the pair as SWISS wall
 * clock time — the same convention TaskFormModal's datetimeLocalToIso
 * follows.
 *
 * This one matters more than the task form's: the backend derives
 * ``work_date`` by converting ``started_at`` into the Europe/Zurich
 * civil date (work_report_service.py). Reading the typed pair in the
 * device's zone therefore did not merely display an hour wrong — a
 * late-evening report filed from a zone ahead of Zurich landed on the
 * previous day, and the backend's "both ends on the same Zurich civil
 * date" rule could reject a perfectly ordinary shift.
 *
 * Returns null for missing/invalid input.
 */
export function dateTimeLocalToIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  return businessDateTimeToIso(dateStr, timeStr);
}

/** ISO datetime → Swiss "HH:MM" for a <input type="time"> value. */
export function isoToTimeInputValue(iso) {
  return isoToBusinessTimeInput(iso);
}

/** ISO datetime → Swiss "YYYY-MM-DD" for a <input type="date"> value. */
export function isoToDateInputValue(iso) {
  return isoToBusinessDateInput(iso);
}

/** Whole minutes between two ISO instants; null when invalid or <= 0.
 * Display-only preview — the backend computes the persisted value. */
export function previewDurationMinutes(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  const mins = Math.floor((end - start) / 60000);
  return mins > 0 ? mins : null;
}

/** "YYYY-MM-DD" work_date → "Mo, 03.08.2026" (de-CH, local civil date —
 * work_date is already a Zurich civil date, so no TZ conversion). */
export function formatWorkDate(workDate) {
  if (!workDate) return "";
  const [y, m, d] = String(workDate).split("-").map(Number);
  if (!y || !m || !d) return String(workDate);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("de-CH", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
